// features/inventory/pages/BatchManagementPage.tsx
/**
 * Batch Management Page — Track and manage product batches with expiry tracking.
 *
 * Route: /inventory/batches
 *
 * Features:
 * - List all batches with expiry status
 * - Filter by expiry status (expired, expiring soon, active)
 * - Search by batch number or product name
 * - Expiry date tracking with visual indicators
 * - Batch quantity tracking
 * - Navigate to product detail
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Pagination } from '@/components/ui/Pagination';
import { getStockTransactions, StockTransaction } from '@/features/stock/services/stockApi';
import {
    ArrowLeft,
    Search,
    X,
    Package,
    RefreshCw,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

type ExpiryFilter = 'all' | 'active' | 'expiring_soon' | 'expired';

interface BatchEntry {
    batchNumber: string;
    productId: string;
    productName: string;
    expiryDate: string | null;
    quantity: number;
    branchName: string;
    lastTransactionDate: string;
    status: 'active' | 'expiring_soon' | 'expired' | 'unknown';
}

// ============================================================
// CONSTANTS
// ============================================================

const PAGE_SIZE = 30;
const EXPIRING_SOON_DAYS = 30;

// ============================================================
// HELPERS
// ============================================================

/**
 * Determine expiry status based on expiry date string.
 * Extracted to avoid spreading StockTransaction type issues.
 */
function getExpiryStatus(expiryDateStr: string | null): BatchEntry['status'] {
    if (!expiryDateStr) return 'unknown';
    const now = new Date();
    const expiry = new Date(expiryDateStr);
    if (isNaN(expiry.getTime())) return 'unknown';
    const daysUntilExpiry = Math.ceil(
        (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= EXPIRING_SOON_DAYS) return 'expiring_soon';
    return 'active';
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function BatchManagementPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);

    // State
    const [search, setSearch] = useState('');
    const [expiryFilter, setExpiryFilter] = useState<ExpiryFilter>('all');
    const [page, setPage] = useState(1);

    // Query transactions to extract batch data
    // We use a large page to get as many transactions as possible for batch extraction
    const { data: txnResponse, isLoading, isError, refetch } = useQuery({
        queryKey: ['batch-transactions', user?.tenantId],
        queryFn: async () => {
            const allData: StockTransaction[] = [];
            let currentPage = 1;
            let hasMore = true;

            // Fetch up to 10 pages to collect batch data
            while (hasMore && currentPage <= 10) {
                const response = await getStockTransactions(user!.tenantId, {
                    limit: 100,
                    page: currentPage,
                });
                const data = response?.data || [];
                allData.push(...data);
                hasMore = data.length === 100;
                currentPage++;
            }

            return allData;
        },
        enabled: !!user?.tenantId,
        staleTime: 5 * 60 * 1000,
    });

    // Extract unique batches from transactions
    // StockTransaction has batchNumber but not expiryDate directly.
    // We use the batchNumber from the transaction and derive status from
    // the fact that if a batch exists, it's active unless we have expiry info.
    const allBatches: BatchEntry[] = useMemo(() => {
        const transactions = (txnResponse || []) as StockTransaction[];
        const batchMap = new Map<string, BatchEntry>();

        transactions.forEach((txn) => {
            // Only process transactions that have a batch number
            if (!txn.batchNumber) return;

            const key = `${txn.productId}-${txn.batchNumber}`;
            const existing = batchMap.get(key);
            const qty = parseFloat(txn.quantity || '0');

            if (!existing) {
                // StockTransaction doesn't have expiryDate in its type definition.
                // We check if it exists at runtime and cast accordingly.
                const txnWithExpiry = txn as StockTransaction & {
                    expiryDate?: string | null;
                };
                const expiryDate = txnWithExpiry.expiryDate || null;
                const status = getExpiryStatus(expiryDate);

                batchMap.set(key, {
                    batchNumber: txn.batchNumber,
                    productId: txn.productId,
                    productName: txn.productName || 'Unknown',
                    expiryDate,
                    quantity: qty,
                    branchName: txn.branchName || 'Unknown',
                    lastTransactionDate: txn.createdAt,
                    status,
                });
            } else {
                existing.quantity += qty;
                if (txn.createdAt > existing.lastTransactionDate) {
                    existing.lastTransactionDate = txn.createdAt;
                }
            }
        });

        return Array.from(batchMap.values());
    }, [txnResponse]);

    // Filter and search
    const filteredBatches = useMemo(() => {
        let result = allBatches;

        if (search) {
            const q = search.toLowerCase();
            result = result.filter(
                (b) =>
                    b.batchNumber.toLowerCase().includes(q) ||
                    b.productName.toLowerCase().includes(q)
            );
        }

        if (expiryFilter !== 'all') {
            result = result.filter((b) => b.status === expiryFilter);
        }

        // Sort by expiry date (expiring soon first)
        result.sort((a, b) => {
            if (!a.expiryDate && !b.expiryDate) return 0;
            if (!a.expiryDate) return 1;
            if (!b.expiryDate) return -1;
            return (
                new Date(a.expiryDate).getTime() -
                new Date(b.expiryDate).getTime()
            );
        });

        return result;
    }, [allBatches, search, expiryFilter]);

    // Pagination
    const totalCount = filteredBatches.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const paginatedBatches = filteredBatches.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    // Stats
    const stats = useMemo(() => {
        return {
            total: allBatches.length,
            active: allBatches.filter(
                (b) => b.status === 'active'
            ).length,
            expiringSoon: allBatches.filter(
                (b) => b.status === 'expiring_soon'
            ).length,
            expired: allBatches.filter(
                (b) => b.status === 'expired'
            ).length,
        };
    }, [allBatches]);

    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-NG', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    const getStatusBadge = (status: BatchEntry['status']) => {
        switch (status) {
            case 'active':
                return (
                    <Badge variant="success" size="sm">
                        Active
                    </Badge>
                );
            case 'expiring_soon':
                return (
                    <Badge variant="warning" size="sm">
                        Expiring Soon
                    </Badge>
                );
            case 'expired':
                return (
                    <Badge variant="danger" size="sm">
                        Expired
                    </Badge>
                );
            default:
                return (
                    <Badge variant="neutral" size="sm">
                        Unknown
                    </Badge>
                );
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() =>
                                    navigate('/inventory')
                                }
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-lg shadow-purple-500/25">
                                <Package className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Batch Management
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {stats.total} batches ·{' '}
                                    {stats.expiringSoon} expiring
                                    soon · {stats.expired} expired
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => refetch()}
                            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                            type="button"
                        >
                            <RefreshCw className="w-4 h-4" />{' '}
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                            Total Batches
                        </p>
                        <p className="text-2xl font-bold text-slate-900">
                            {stats.total}
                        </p>
                    </div>
                    <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-4 text-center">
                        <p className="text-[10px] text-emerald-600 uppercase tracking-wider">
                            Active
                        </p>
                        <p className="text-2xl font-bold text-emerald-700">
                            {stats.active}
                        </p>
                    </div>
                    <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4 text-center">
                        <p className="text-[10px] text-amber-600 uppercase tracking-wider">
                            Expiring Soon (&lt;
                            {EXPIRING_SOON_DAYS}d)
                        </p>
                        <p className="text-2xl font-bold text-amber-700">
                            {stats.expiringSoon}
                        </p>
                    </div>
                    <div className="bg-rose-50 rounded-2xl border border-rose-200 p-4 text-center">
                        <p className="text-[10px] text-rose-600 uppercase tracking-wider">
                            Expired
                        </p>
                        <p className="text-2xl font-bold text-rose-700">
                            {stats.expired}
                        </p>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5">
                    <div className="flex flex-col lg:flex-row gap-1.5">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by batch number or product name..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(
                                        e.target.value
                                    );
                                    setPage(1);
                                }}
                                className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 text-sm"
                            />
                            {search && (
                                <button
                                    onClick={() => {
                                        setSearch('');
                                        setPage(1);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-400 hover:text-slate-600"
                                    type="button"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                            {([
                                {
                                    value: 'all' as ExpiryFilter,
                                    label: 'All',
                                },
                                {
                                    value: 'active' as ExpiryFilter,
                                    label: 'Active',
                                },
                                {
                                    value: 'expiring_soon' as ExpiryFilter,
                                    label: 'Expiring Soon',
                                },
                                {
                                    value: 'expired' as ExpiryFilter,
                                    label: 'Expired',
                                },
                            ]).map((f) => (
                                <button
                                    key={f.value}
                                    onClick={() => {
                                        setExpiryFilter(
                                            f.value
                                        );
                                        setPage(1);
                                    }}
                                    className={clsx(
                                        'px-3 py-2 rounded-lg text-xs font-medium transition-all',
                                        expiryFilter ===
                                            f.value
                                            ? 'bg-white text-purple-600 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                    )}
                                    type="button"
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Batches Table */}
                {isLoading ? (
                    <div className="flex justify-center py-20">
                        <Spinner size="xl" />
                    </div>
                ) : isError ? (
                    <ErrorState
                        title="Failed to load batches"
                        onRetry={() => refetch()}
                    />
                ) : paginatedBatches.length === 0 ? (
                    <EmptyState
                        icon={
                            <Package className="w-16 h-16" />
                        }
                        title="No batches found"
                        description={
                            search ||
                                expiryFilter !== 'all'
                                ? 'Try adjusting your filters.'
                                : 'Batch tracking data will appear when products with batch numbers are transacted.'
                        }
                    />
                ) : (
                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead>
                                    <tr className="bg-slate-50/50">
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                            Batch Number
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                            Product
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                            Branch
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                            Quantity
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">
                                            Expiry Date
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                            Last Activity
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {paginatedBatches.map(
                                        (batch, idx) => (
                                            <motion.tr
                                                key={`${batch.productId}-${batch.batchNumber}`}
                                                initial={{
                                                    opacity: 0,
                                                    y: 10,
                                                }}
                                                animate={{
                                                    opacity: 1,
                                                    y: 0,
                                                }}
                                                transition={{
                                                    delay:
                                                        idx *
                                                        0.02,
                                                }}
                                                className={clsx(
                                                    'hover:bg-slate-50 transition-colors cursor-pointer',
                                                    batch.status ===
                                                    'expired' &&
                                                    'bg-rose-50/20',
                                                    batch.status ===
                                                    'expiring_soon' &&
                                                    'bg-amber-50/20'
                                                )}
                                                onClick={() =>
                                                    navigate(
                                                        `/inventory/levels/${batch.productId}`
                                                    )
                                                }
                                            >
                                                <td className="px-4 py-3">
                                                    <span className="text-sm font-mono font-semibold text-purple-700">
                                                        {
                                                            batch.batchNumber
                                                        }
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm font-medium text-slate-700">
                                                    {
                                                        batch.productName
                                                    }
                                                </td>
                                                <td className="px-4 py-3 text-sm text-slate-500">
                                                    {
                                                        batch.branchName
                                                    }
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm font-semibold text-slate-700 tabular-nums">
                                                    {batch.quantity.toLocaleString()}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span
                                                        className={clsx(
                                                            'text-sm font-medium',
                                                            batch.status ===
                                                                'expired'
                                                                ? 'text-rose-600'
                                                                : batch.status ===
                                                                    'expiring_soon'
                                                                    ? 'text-amber-600'
                                                                    : 'text-slate-600'
                                                        )}
                                                    >
                                                        {formatDate(
                                                            batch.expiryDate
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    {getStatusBadge(
                                                        batch.status
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-xs text-slate-400">
                                                    {formatDate(
                                                        batch.lastTransactionDate
                                                    )}
                                                </td>
                                            </motion.tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {totalPages > 1 && (
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalItems={totalCount}
                                pageSize={PAGE_SIZE}
                                onPageChange={setPage}
                                onPageSizeChange={() => { }}
                            />
                        )}
                    </Card>
                )}
            </div>
        </div>
    );
}